import { Link } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { comparisons } from '@/content/comparisons';
import { useSeo } from '@/seo/useSeo';

export function Compare(): JSX.Element {
  useSeo({
    title: 'Compare Nexus to other RMM & PSA platforms',
    description:
      'How Nexus compares to NinjaOne, Syncro, Atera, and ConnectWise — vendor-neutral questions worth asking any of them, and where Nexus stands today, honestly.',
    path: '/compare',
    schema: {
      '@type': 'ItemList',
      itemListElement: comparisons.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://nexusrmm.ai/compare/${c.slug}`,
        name: `Nexus vs. ${c.vendor}`,
      })),
    },
  });

  return (
    <div>
      <PageHero
        eyebrow="Compare"
        title="How Nexus compares — and the questions worth asking any vendor."
        subtitle="A feature-comparison table is the easiest thing for any vendor, us included, to make look favorable. These pages ask the questions we think actually predict whether a platform holds up a year in, and say plainly where Nexus stands today."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {comparisons.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.slug} delay={(i % 2) * 90}>
                  <Link
                    to={`/compare/${c.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                  >
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                      <Icon fontSize="small" />
                    </div>
                    <h2 className="font-display text-[17px] font-semibold text-nexus-ink">
                      Nexus vs. {c.vendor}
                    </h2>
                    <p className="mt-1.5 flex-1 text-sm text-white/65">{c.summary}</p>
                    <Chip
                      label={c.category}
                      size="small"
                      className="mt-4 h-[22px] w-fit border border-white/15 bg-transparent text-[10.5px] font-semibold text-white/55"
                    />
                  </Link>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={200}>
            <p className="mt-10 max-w-[720px] text-sm text-white/50">
              We don&apos;t have verified inside knowledge of any of these vendors&apos; internals, so
              every page below asks questions a prospect can put to that vendor directly rather than
              asserting an answer for them — the same standard our own{' '}
              <Link to="/buyers-guide" className="text-nexus-purple-light no-underline hover:underline">
                buyer&apos;s guide
              </Link>{' '}
              holds us to. Pricing and feature sets change; verify current details directly with each
              vendor.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                See the architecture behind the answers.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort and put these questions to the team actually building
                it, not a sales script.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
